import { Outlet, useLocation } from "react-router-dom";
import React, { Suspense, useMemo } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useDispatch } from "react-redux";

import { Header } from "@/components/layout/header";
import { Sidebar } from "@/components/layout/sidebar";
import { RouteFallback } from "@/components/layout/RouteFallback";
import { DESKTOP_QUERY, PANEL_SURFACE } from "@/components/layout/layout.constants";
import { NotificationPermissionDialog } from "@/components/NotificationPermissionDialog";
import { selectUser, setAnnouncementId } from "@/store/slices/user.slice";
import { useAppSelector } from "@/hooks/useReduxHooks";
import AnnouncementModal from "@/components/announcement/AnnouncementModal";
import { PageContent } from "@/components/layout/split-screen";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import SocketStatusBanner from "@/components/common/SocketStatusBanner";
import {
  MayaCopilot,
  MayaPageSeam,
  MayaSidebar,
  MayaSidebarProvider,
} from "@/components/layout/header/maya-copilot";
import { cn } from "@/utils/cn";
import { ChatMessagePopups } from "@/components/layout/ChatMessagePopups";
import { ChatWindows } from "@/components/layout/chat-windows/ChatWindows";
import { useResponsiveSidebar } from "./_hooks/useResponsiveSidebar";
import { useAnnouncementQueue } from "./_hooks/useAnnouncementQueue";
import {
  SettingsShellContext,
  SettingsSidebar,
  SidebarSlot,
  useRememberAppLocation,
  useSettingsRouteNav,
} from "@/components/layout/settings";

const Layout: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  const { isSidebarOpen, toggleSidebar, closeSidebar, openSidebar } =
    useResponsiveSidebar();
  const { activeAnnouncementId, closeActive } = useAnnouncementQueue();
  const { announcementId } = useAppSelector(selectUser);

  const hideLayout = useMemo(
    () =>
      location.pathname.includes("/auth") ||
      location.pathname.includes("/company/add-company") ||
      /^\/documents\/sign(\/|$)/.test(location.pathname),
    [location],
  );
  // Main Settings swaps the main menu for its own sidebar (see below).
  const settingsNav = useSettingsRouteNav();
  const settingsShell = useMemo(() => ({ openNav: openSidebar }), [openSidebar]);
  // Settings' back link returns to the last page shown here — not a settings
  // page, and not the chrome-less screens above.
  useRememberAppLocation(!hideLayout && !settingsNav);

  if (hideLayout) {
    return (
      // dvh, not vh: on phones 100vh is taller than the screen while the
      // browser bar shows, pushing the bottom (e.g. chat's composer) out of view.
      <div className="flex h-dvh overflow-hidden">
        <div className="flex flex-col flex-1 overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    );
  }

  return (
    <MayaSidebarProvider>
      <div className="flex flex-col h-dvh overflow-hidden bg-muted">
        <ImpersonationBanner />
        <SocketStatusBanner />

        {/* ── 3 separate cards: full-width top bar → sidebar │ page ── */}
        <div className="flex flex-col flex-1 min-h-0 gap-2 overflow-hidden p-2">
          {/* Top bar — spans full width, logo → user avatar */}
          <div className={cn(PANEL_SURFACE, "shrink-0 bg-white overflow-hidden")}>
            <Header
              isSidebarOpen={isSidebarOpen}
              onToggleSidebar={toggleSidebar}
              isDesktop={isDesktop}
            />
          </div>

          {/* Body — sidebar on left, active page, Maya's chat on right (when open) */}
          <div className="flex flex-1 min-h-0 gap-2 overflow-hidden">
            {/* On main Settings the settings sidebar slides in over the main
                menu, which stays mounted underneath — see SidebarSlot. */}
            <SidebarSlot
              isDesktop={isDesktop}
              mainMenu={
                <Sidebar
                  isSidebarOpen={isSidebarOpen}
                  isDesktop={isDesktop}
                  onClose={closeSidebar}
                />
              }
              settingsMenu={
                settingsNav ? (
                  // Keyed by space: My Space and Workspace settings are
                  // different lists, so neither inherits the other's state.
                  <SettingsSidebar
                    key={settingsNav.space}
                    nav={settingsNav}
                    isSidebarOpen={isSidebarOpen}
                    isDesktop={isDesktop}
                    onClose={closeSidebar}
                  />
                ) : null
              }
            />

            <div
              className={cn(
                PANEL_SURFACE,
                "relative flex flex-col flex-1 min-w-0 bg-muted overflow-hidden",
              )}
            >
              {/* Light on the edge facing Maya while Maya can see this page. */}
              <MayaPageSeam isDesktop={isDesktop} />
              <SettingsShellContext.Provider value={settingsShell}>
                <PageContent />
              </SettingsShellContext.Provider>
              <NotificationPermissionDialog />

              {/* Sequential unread popups */}
              {activeAnnouncementId && (
                <AnnouncementModal
                  isFromCompany={false}
                  announcementId={activeAnnouncementId}
                  onClose={closeActive}
                />
              )}

              {/* Socket-based announcement popup */}
              {announcementId && (
                <AnnouncementModal
                  isFromCompany={false}
                  announcementId={announcementId}
                  onClose={() => dispatch(setAnnouncementId(null))}
                />
              )}
            </div>

            {/* Maya's chat sits beside the page it can see (overlay below lg). */}
            <MayaSidebar isDesktop={isDesktop} />
          </div>
        </div>

        {/* App-level draggable Maya launcher (portals to body) — opens the sidebar */}
        <MayaCopilot />
        <ChatMessagePopups />
        <ChatWindows />
      </div>
    </MayaSidebarProvider>
  );
};

export default React.memo(Layout);
